import React, { FC, HTMLAttributes } from 'react'
import { useParams, useHistory, useLocation } from 'react-router'
import { Card, ImageBox, Counter } from '@engyalo/storefront-components'
import { viewProduct, addProduct, removeProduct, updateQuantity } from '@engyalo/storefront-events'
import { Hook } from '@engyalo/storefront-types'
import { Box, Heading, Skeleton } from '@chakra-ui/core'
import { Price } from '../Price'
import { ICartDictionary, IProduct } from '@engyalo/storefront-types/lib/plugins/model'
import { getStoreFront_orderConfig as IOrderConfig } from '@engyalo/storefront-types/lib/graphql/storefront'
import { useRecoilState, useRecoilValue } from 'recoil'
import { cartState, configState, orderConfigState } from '../../recoil/atoms'
import Plugin from '../../Plugin'
import { generateCartId } from '../../lib/cartIdGenerator'
import { getTotalPrice } from '../../lib/getTotalPrice'
import { getActionScreen } from '../../utils/analytics'
import { ILocationSearch, skipIfInPreviewMode } from '../../utils/mode'
import { IConfig } from '../../recoil/types'
import { useUnitTypes } from '../../hooks/useUnitTypes'
interface IProps extends HTMLAttributes<HTMLDivElement> {
  product: IProduct
  isLoading: boolean
  showPrice: boolean
}

interface IRunConditionallyParams {
  condition: () => boolean
  after?: () => void
  before?: () => void
}

const runConditionally = ({ condition, after, before }: IRunConditionallyParams) => {
  before?.()

  if (condition()) {
    return
  }

  after?.()
}

export const ProductItem: FC<IProps> = ({ product, isLoading, showPrice }: IProps) => {
  const [cart, setCart] = useRecoilState<ICartDictionary>(cartState)
  const config = useRecoilValue<IConfig>(configState)
  const orderConfig = useRecoilValue<IOrderConfig>(orderConfigState)
  const history = useHistory()
  const location = useLocation<ILocationSearch>()
  const { customer, order } = useParams()
  const { unitType, hasUnitTypes } = useUnitTypes(cart, product)

  const session = {
    userID: orderConfig.userID || '',
    customer: config.customer,
  }

  const getProductFirstImage = (product: IProduct): string => {
    if (product && product.imageURL) return product.imageURL[0] || ''
    return ''
  }

  const addToCart = (product: IProduct, quantity = 1, shouldReplace = false): void => {
    const tempCart = { ...cart }
    if (product && product.id) {
      const currentSku = generateCartId(product)
      if (tempCart[currentSku]) {
        const tempProd = { ...tempCart[currentSku] }
        if (tempProd.quantity) {
          const totalPrice = getTotalPrice(product)
          tempProd.quantity = shouldReplace ? quantity : tempProd.quantity + quantity
          if (tempProd.quantity > 0) {
            tempCart[currentSku] = {
              ...tempProd,
              totalPrice,
            }
            setCart(tempCart)
          } else {
            delete tempCart[currentSku]
            setCart({ ...tempCart })
          }
        }
      } else if (quantity > 0) {
        const totalPrice = getTotalPrice(product)
        tempCart[currentSku] = {
          ...product,
          totalPrice,
          quantity,
          cartId: currentSku,
        } as IProduct
      }
      setCart(tempCart)
    }
  }

  if (!product) {
    return null
  }

  return (
    <Box
      onClick={(): void => {
        if (!config.enabledProductDetail) {
          return
        }

        skipIfInPreviewMode(() => {
          viewProduct({
            product: {
              id: product.id || '',
              sku: product.sku || '',
              name: product.name || '',
              price: product.price || 0,
              category: product.category || '',
              screen: getActionScreen(location),
            },
            session,
          })
        })
        history.push({
          pathname: `/${customer}/${order}/product`,
          state: { product },
          search: location.search,
        })
      }}
    >
      <Plugin
        hook={Hook.COLLECTION_PRODUCT}
        pluginProps={{
          product,
          currency: config.currency,
          currencyPrefix: config.currencyPrefix,
          currencyFormat: config.currencyFormat,
          cart,
          showPrice: showPrice,
          isLoading: isLoading,
        }}
      >
        <Card>
          <Skeleton isLoaded={!isLoading} w="100%" h={isLoading ? '130px' : 'unset'}>
            <ImageBox imageUrl={getProductFirstImage(product)} />
          </Skeleton>
          <Skeleton
            isLoaded={!isLoading}
            w={isLoading ? '40%' : 'unset'}
            h={isLoading ? '16px' : 'unset'}
            flex={isLoading ? 'unset' : '1'}
            marginY="4px"
          >
            <Heading as="h3" size="sm" fontSize="productTitle">
              {product.name}
            </Heading>
          </Skeleton>
          <Skeleton
            isLoaded={!isLoading}
            w={isLoading ? '100%' : 'unset'}
            h={isLoading ? '16px' : 'unset'}
            paddingBottom="4px"
          >
            <Box fontSize="productDescription" fontFamily="body" color="gray.100">
              {product.description}
            </Box>
          </Skeleton>

          {showPrice ? (
            <Skeleton
              isLoaded={!isLoading}
              w={isLoading ? '40%' : 'unset'}
              h={isLoading ? '16px' : 'unset'}
              flex={isLoading ? 'unset' : '1'}
              marginY="4px"
            >
              <Price price={product.price} currency={config.currencyPrefix} currencyFormat={config.currencyFormat} />
            </Skeleton>
          ) : null}

          <Skeleton isLoaded={!isLoading} w="100%">
            <Counter
              name={product.sku || ''}
              addAction={(e): void => {
                runConditionally({
                  condition() {
                    return hasUnitTypes
                  },
                  before() {
                    skipIfInPreviewMode(() => {
                      addProduct({
                        product: {
                          id: product.id || '',
                          sku: product.sku || '',
                          name: product.name || '',
                          price: product.price || 0,
                          category: product.category || '',
                          screen: getActionScreen(location),
                          note: '',
                        },
                        session,
                      })
                    })
                  },
                  after() {
                    e.stopPropagation()
                    addToCart(product, 1)
                  },
                })
              }}
              minusAction={(e): void => {
                runConditionally({
                  condition() {
                    return hasUnitTypes
                  },
                  before() {
                    skipIfInPreviewMode(() => {
                      removeProduct({
                        product: {
                          id: product.id || '',
                          sku: product.sku || '',
                          name: product.name || '',
                          price: product.price || 0,
                          category: product.category || '',
                          screen: getActionScreen(location),
                          note: '',
                        },
                        session,
                      })
                    })
                  },
                  after() {
                    e.stopPropagation()
                    addToCart(product, -1)
                  },
                })
              }}
              onInputBlur={(event: React.FocusEvent<HTMLInputElement>): void => {
                runConditionally({
                  condition() {
                    return hasUnitTypes
                  },
                  after() {
                    const quantity = parseInt(event.target.value) || 0
                    skipIfInPreviewMode(() => {
                      updateQuantity({
                        product: {
                          id: product.id || '',
                          sku: product.sku || '',
                          name: product.name || '',
                          category: product.category || '',
                          price: product.price || 0,
                          note: '',
                          quantity,
                          screen: getActionScreen(location),
                        },
                        session,
                      })
                    })
                    addToCart(product, quantity, true)
                  },
                })
              }}
              value={cart[generateCartId(product) || ''] ? cart[generateCartId(product) || ''].quantity : 0}
              format={unitType?.format}
            />
          </Skeleton>
        </Card>
      </Plugin>
    </Box>
  )
}
